/**
 * Библиотека торговых идей.
 *
 * Шаблон — не отдельный вид индикатора, а способ не начинать с пустого
 * экрана. Он собирает обычное описание из обычных блоков, и сразу после
 * создания человек волен менять в нём всё: пороги, условия, стороны. Ни
 * одного поля, недоступного тому, кто собрал бы то же самое руками, у
 * шаблона нет — иначе «готовая стратегия» стала бы чёрным ящиком, а
 * платформа обещает обратное.
 *
 * У каждой идеи — паспорт: для кого она, для какого рынка и горизонта,
 * на каком таймфрейме её разумно начинать, и три абзаца простыми
 * словами — что происходит на рынке, почему это работает и когда
 * ломается. **Ни один шаблон не обещает прибыли**: сколько раз идея
 * сработала и куда потом шла цена, считает движок на истории монеты
 * человека, и только он.
 *
 * Названия школ оставлены как есть — ICT, Smart Money, FVG: тот, кто их
 * ищет, ищет именно эти слова.
 *
 * Сами идеи лежат по разделам в `templates/`, кирпичики — в
 * `templates/kit.ts`.
 */
import { BREAKOUT_TEMPLATES } from './templates/breakout.js';
import { CLASSIC_TEMPLATES } from './templates/classic.js';
import { CRYPTO_TEMPLATES } from './templates/crypto.js';
import { ICT_TEMPLATES } from './templates/ict.js';
import { MULTI_TF_TEMPLATES } from './templates/multi-tf.js';
import { OSCILLATOR_TEMPLATES } from './templates/oscillators.js';
import { PRICE_ACTION_TEMPLATES } from './templates/price-action.js';
import { RANGE_TEMPLATES } from './templates/range.js';
import { REVERSAL_TEMPLATES } from './templates/reversal.js';
import { SCALPING_TEMPLATES } from './templates/scalping.js';
import { SMART_MONEY_TEMPLATES } from './templates/smart-money.js';
import { TREND_TEMPLATES } from './templates/trend.js';
import { VOLUME_TEMPLATES } from './templates/volume.js';
import { MORE_TEMPLATES } from './templates/more.js';
import { WAVE2_TEMPLATES } from './templates/wave2.js';
/**
 * Разделы библиотеки — регионы карты.
 *
 * Порядок не алфавитный: сверху то, что ищут чаще и с чего проще начать.
 * Smart Money и ICT стоят рядом и отдельно от осцилляторов: человек,
 * пришедший за ними, за RSI не пришёл.
 */
export const TEMPLATE_CATEGORIES = [
    { id: 'trend', title: 'Тренд', icon: '📈', hint: 'Идти туда, куда рынок уже идёт' },
    { id: 'reversal', title: 'Разворот', icon: '🔄', hint: 'Ловить смену направления' },
    { id: 'breakout', title: 'Пробой', icon: '💥', hint: 'Выход цены за привычные границы' },
    { id: 'classic', title: 'Классика трейдинга', icon: '🏆', hint: 'Знаменитые стратегии под своими именами' },
    { id: 'smart-money', title: 'Smart Money', icon: '🧠', hint: 'Структура, ликвидность, зоны' },
    { id: 'ict', title: 'ICT', icon: '🏛', hint: 'Готовые связки школы ICT' },
    { id: 'crypto', title: 'Крипто-сигналы', icon: '🪙', hint: 'То, чего нет на обычных биржах: BTC, стейблы, спот и фьючерсы' },
    { id: 'volume', title: 'Объём', icon: '📊', hint: 'Движение, подтверждённое участием' },
    { id: 'price-action', title: 'Price Action', icon: '🕯', hint: 'Уровни и форма свечей' },
    { id: 'indicators', title: 'Осцилляторы', icon: '📐', hint: 'RSI, MACD, Stochastic, CCI — классика импульса' },
    { id: 'scalping', title: 'Скальпинг', icon: '⚡', hint: 'Частые входы на младших таймфреймах' },
    { id: 'range', title: 'Боковик', icon: '🌊', hint: 'Когда тренда нет' },
    { id: 'multi-tf', title: 'Мульти-таймфрейм', icon: '⏱', hint: 'Старший таймфрейм решает, младший входит' },
];
export const LEVEL_TEXT = { novice: 'Новичку', skilled: 'Опытному', pro: 'Профи' };
export const MARKET_TEXT = { trend: 'Для тренда', range: 'Для боковика', any: 'Любой рынок' };
export const HORIZON_TEXT = { scalp: 'Скальп', day: 'Внутри дня', swing: 'На дни и недели' };
export const STYLE_TEXT = {
    'with-trend': 'По тренду',
    'counter-trend': 'Против движения',
    breakout: 'На пробое',
    alert: 'Предупреждение',
};
/* ------------------------------------------------------------- шаблоны */
export const SIGNAL_TEMPLATES = [
    ...TREND_TEMPLATES,
    ...REVERSAL_TEMPLATES,
    ...BREAKOUT_TEMPLATES,
    ...CLASSIC_TEMPLATES,
    ...SMART_MONEY_TEMPLATES,
    ...ICT_TEMPLATES,
    ...CRYPTO_TEMPLATES,
    ...VOLUME_TEMPLATES,
    ...PRICE_ACTION_TEMPLATES,
    ...OSCILLATOR_TEMPLATES,
    ...SCALPING_TEMPLATES,
    ...RANGE_TEMPLATES,
    ...MULTI_TF_TEMPLATES,
    ...MORE_TEMPLATES,
    ...WAVE2_TEMPLATES,
];
/* ------------------------------------------------------------- поиск */
export function templateById(id) {
    return SIGNAL_TEMPLATES.find((template) => template.id === id) ?? null;
}
/** Шаблоны раздела — в том же порядке, что объявлены. */
export function templatesOf(category) {
    return SIGNAL_TEMPLATES.filter((template) => template.category === category);
}
/** То, что показывают первым: с чего начать. */
export function popularTemplates() {
    return SIGNAL_TEMPLATES.filter((template) => template.popular === true);
}
/**
 * Поиск по библиотеке.
 *
 * Ищет не только по названию: человек набирает «ликвидность» и должен
 * получить и ICT Reversal, и Liquidity Raid. Поэтому в поиск идут шаги,
 * описание, паспорт и слова, под которыми школа знает эту идею.
 */
export function searchTemplates(query) {
    const needle = query.trim().toLowerCase();
    if (needle === '')
        return [];
    return SIGNAL_TEMPLATES.filter((template) => [template.name, template.hint, ...template.steps, ...(template.keywords ?? []), template.passport.idea]
        .join(' ')
        .toLowerCase()
        .includes(needle));
}
/** Разделы вместе с их шаблонами; пустые разделы не показываются. */
export function templateCategories() {
    return TEMPLATE_CATEGORIES.map((category) => ({ category, items: templatesOf(category.id) })).filter((group) => group.items.length > 0);
}
/** Шаблоны по разделам — под прежним именем: терминал звал так. */
export function templateFamilies() {
    return templateCategories().map((group) => ({ family: group.category.title, items: group.items }));
}
//# sourceMappingURL=signal-templates.js.map